// Shared post-authentication routing rules.
//
// Both the login screen and the login OTP screen have to answer the same
// question once a user is authenticated: where does this user land? Keeping the
// rules here means the OTP step and the login response handler can never drift
// apart.

import { isLiveSubscriptionStatus } from './subscriptionTrial';

export const isTenantRole = (role) =>
  role === '2' || role === 2 || role === 'tenant';

// Pulls the fields the routing rules depend on out of whichever response shape
// the API returned (some endpoints nest under `data`, some under `user_details`).
export const getUserDetails = (response) => {
  const userData = response?.data || response;
  return userData?.user_details || userData || {};
};

export const getRoleFromResponse = (response) => {
  const userData = response?.data || response;
  return userData?.user_details?.role ?? userData?.role ?? response?.role;
};

export const getEmailFromResponse = (response) => {
  const userData = response?.data || response;
  return userData?.user_details?.email || userData?.email || response?.email;
};

export const getTokenFromResponse = (response) => {
  const userData = response?.data || response;
  return userData?.user_details?.token || userData?.token || response?.token;
};

// Landlords without a live subscription have to finish payment first. A trial
// counts as live: the backend starts one automatically for eligible landlords,
// and they get the dashboard without paying anything up front.
const needsPayment = (details) => {
  const subscriptions = details?.UserSubscription;
  if (Array.isArray(subscriptions) && subscriptions.length === 0) {
    return true;
  }
  const userSubscription = Array.isArray(subscriptions) ? subscriptions[0] : subscriptions;
  if (!userSubscription) return false;
  if (isLiveSubscriptionStatus(userSubscription?.status)) return false;
  return userSubscription?.status === 'incomplete';
};

/**
 * Resolves the route an authenticated user should land on.
 *
 * @param {object} response  Login / OTP verification API response.
 * @param {string} [redirectTo]  Optional `?redirect=` target from the URL.
 * @returns {string} Path to navigate to.
 */
export const getPostLoginRoute = (response, redirectTo) => {
  const details = getUserDetails(response);
  const role = getRoleFromResponse(response);

  // An unverified email always wins: the signup verification screen comes first.
  if (details?.is_email_verified === false) {
    return '/verify-otp';
  }

  if (redirectTo) {
    return redirectTo.startsWith('/') ? redirectTo : `/${redirectTo}`;
  }

  if (isTenantRole(role)) {
    return '/tenant/properties';
  }

  return needsPayment(details) ? '/complete-payment' : '/landlord/dashboard';
};

/**
 * Persists an authenticated session. Only called once the user has cleared the
 * OTP challenge — before that there is no session to store.
 */
export const persistSession = (response) => {
  const userData = response?.data || response;
  const token = getTokenFromResponse(response);
  const role = getRoleFromResponse(response);

  try {
    localStorage.setItem('loginResponse', JSON.stringify(response));
    if (userData) {
      localStorage.setItem('userData', JSON.stringify(userData));
    }
    if (token) {
      localStorage.setItem('token', token);
    }
    if (role !== undefined && role !== null && role !== '') {
      localStorage.setItem('role', String(role));
    }
  } catch (storageError) {
    console.error('Error storing login data:', storageError);
  }
};

// Scratch data kept only between the login form and the OTP challenge.
export const PENDING_LOGIN_KEYS = {
  email: 'pendingLoginEmail',
  role: 'pendingLoginRole',
  response: 'pendingLoginResponse',
};

export const clearPendingLogin = () => {
  Object.values(PENDING_LOGIN_KEYS).forEach((key) => localStorage.removeItem(key));
};

/**
 * Drops every trace of a session (token, userData, role, onboarding scratch
 * data). Used wherever the next screen must start from a clean slate: the end
 * of landlord onboarding, and the start of a fresh signup / login. Without this
 * a token written before the OTP challenge could still unlock the dashboard on
 * a refresh.
 */
export const clearSession = () => {
  try {
    localStorage.clear();
  } catch (storageError) {
    console.error('Error clearing session data:', storageError);
  }
};
